import { useEffect, useMemo, useState } from "react";
import Table from "@/components/Table/Table";
import type { Column } from "@/types/common";
import ticketPaymentService from "@/services/api/ticketPayments";
import type { TicketPayment } from "@/services/api/ticketPayments";
import { getDisplayName } from "@/lib/user";
import { formatINR } from "@/lib/currency";
import { formatDate } from "@/lib/dateFormat";
import { captalizeEachWord } from "@/lib/utils";
import { usePageSubtitle } from "@/hooks/usePageSubtitle";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { getTicketPayments } from "@/store/slices/ticketPaymentSlice";
import { getEvents } from "@/store/slices/eventSlice";
import ExportConfirmationModal from "@/components/ExportConfirmationModal/ExportConfirmationModal";
import { StatCardsRow } from "@/components/StatCard/StatCard";
import type { StatCardData } from "@/components/StatCard/StatCard";
import StatusBadge from "@/components/StatusBadge/StatusBadge";
import type { StatusTone } from "@/components/StatusBadge/StatusBadge";
import CustomTabs from "@/components/CustomTabs/CustomTabs";
import type { CustomTab } from "@/components/CustomTabs/CustomTabs";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import Eye from "@/components/SVGComponents/Eye";
import MailIcon from "@/components/SVGComponents/MailIcon";
import DownloadIcon from "@/components/SVGComponents/DownloadIcon";
import ClockIcon from "@/components/SVGComponents/ClockIcon";
import SearchIcon from "@/components/SVGComponents/SearchIcon";
import CrossIcon from "@/components/SVGComponents/CrossIcon";
import ChevronDownIcon from "@/components/SVGComponents/ChevronDownIcon";
import Button from "@/components/Button/Button";
import Pagination from "@/components/Table/Pagination";
import toast from "@/lib/toast";
import TicketCard from "./components/TicketCard";
import styles from "./Ticketing.module.scss";
import { TableCellTooltip } from "@/components/Tooltip";

const PAYMENT_TYPE_OPTIONS = [
  { label: "Card", value: "card" },
  { label: "Netbanking", value: "netbanking" },
  { label: "Wallet", value: "wallet" },
  { label: "UPI", value: "upi" },
  { label: "Emandate", value: "emandate" },
];

const ORDER_STATUS_TABS: CustomTab[] = [
  { label: "All orders", value: "" },
  { label: "Paid", value: "SUCCESS" },
  { label: "Pending", value: "PENDING" },
  { label: "Failed", value: "FAILED" },
];

const getOrderStatusTone = (status: string | null | undefined): StatusTone => {
  const normalized = (status ?? "").toUpperCase();
  if (normalized === "SUCCESS") return "success";
  if (normalized === "FAILED") return "danger";
  if (normalized === "PENDING") return "warning";
  return "neutral";
};

const formatMethod = (row: TicketPayment) => {
  if (row.paymentMethodDetail) return row.paymentMethodDetail;
  if (!row.paymentType) return "—";
  return captalizeEachWord(row.paymentType);
};

const getRowActions = (order: TicketPayment): RowAction[] => {
  const name = getDisplayName(order, { fallbackLabel: "Member" });
  return [
    {
      key: "view",
      label: "View order",
      icon: <Eye width={16} height={16} strokeWidth={1.75} />,
      onClick: () => toast.warning(`Viewing ${name}'s order isn't wired up yet.`),
    },
    {
      key: "receipt",
      label: "Resend receipt",
      icon: <MailIcon />,
      onClick: () => toast.warning(`Resending a receipt to ${name} isn't wired up yet.`),
    },
    {
      key: "export",
      label: "Export this row",
      icon: <DownloadIcon />,
      onClick: () => toast.warning(`Exporting a single row isn't wired up yet.`),
    },
    {
      key: "refund",
      label: "Process refund",
      icon: <ClockIcon />,
      dividerBefore: true,
      onClick: () => toast.warning(`Processing a refund for ${name} isn't wired up yet.`),
    },
  ];
};

const Ticketing = () => {
  const dispatch = useAppDispatch();
  const { ticketPayments, totalTicketPayments, ticketRevenue, totalGstCollected, failedPayments, isFetching } =
    useAppSelector((state) => state.ticketPayment);
  const { events } = useAppSelector((state) => state.event);

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState("");
  const [gatheringFilter, setGatheringFilter] = useState<string | null>(null);
  const [paymentTypeFilter, setPaymentTypeFilter] = useState<string | null>(null);
  const [pageSelected, setPageSelected] = useState(1);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const rowsPerPageValue = 10;

  useEffect(() => {
    dispatch(getEvents({ perPage: 100 }));
  }, [dispatch]);

  const formConfig = useMemo(
    () => ({
      eventId: {
        controlType: "select" as const,
        label: "Gathering",
        isControlled: true,
        props: { options: events.map((event) => ({ label: event.name, value: String(event.id) })) },
      },
      paymentType: {
        controlType: "select" as const,
        label: "Method",
        isControlled: true,
        props: { options: PAYMENT_TYPE_OPTIONS },
      },
    }),
    [events],
  );

  useEffect(() => {
    setPageSelected(1);
  }, [statusFilter]);

  useEffect(() => {
    dispatch(
      getTicketPayments({
        pageNo: pageSelected,
        perPage: rowsPerPageValue,
        search: debouncedSearch || undefined,
        paymentStatus: statusFilter || undefined,
        eventId: gatheringFilter ? Number(gatheringFilter) : undefined,
        paymentType: paymentTypeFilter || undefined,
      }),
    );
  }, [dispatch, debouncedSearch, pageSelected, statusFilter, gatheringFilter, paymentTypeFilter]);

  usePageSubtitle(isFetching ? "Loading…" : `${totalTicketPayments} orders`);

  const ticketingStats: StatCardData[] = useMemo(
    () => [
      { label: "Gross ticket revenue", value: formatINR(ticketRevenue) },
      { label: "GST collected (18%)", value: formatINR(totalGstCollected) },
      {
        label: "Failed payments",
        value: failedPayments,
        ...(failedPayments > 0 ? { valueTone: "danger" as const } : {}),
      },
    ],
    [ticketRevenue, totalGstCollected, failedPayments],
  );

  const columns: Column<TicketPayment>[] = [
    { name: "Order", width: "12%", selector: (row) => row?.paymentId || "-" },
    {
      name: "Member",
      width: "12%",
      cell: (row) => (
        <div className={styles.member}>
          <span className={styles.memberName}>
            <TableCellTooltip value={getDisplayName(row, { fallbackLabel: "Member" })} />
          </span>
        </div>
      ),
    },
    { name: "Gathering", width: "14%", selector: (row) => row?.eventName || "—" },
    { name: "Ticket", width: "9%", selector: (row) => formatINR(row?.ticketAmount) },
    { name: "GST", width: "8%", selector: (row) => formatINR(row?.taxAmount) },
    { name: "Total", width: "9%", selector: (row) => formatINR(row?.totalAmount) },
    { name: "Method", width: "12%", selector: (row) => formatMethod(row) },
    { name: "Placed", width: "12%", selector: (row) => formatDate(row?.createdAt, { day: "numeric", month: "short" }) },
    {
      name: "Status",
      width: "12%",
      cell: (row) => <StatusBadge label={row.paymentStatus || "—"} tone={getOrderStatusTone(row.paymentStatus)} />,
    },
  ];

  return (
    <>
      <div className={styles.statsContainer}>
        <StatCardsRow stats={ticketingStats} />
      </div>

      <div className={styles.toolbar}>
        <CustomTabs tabs={ORDER_STATUS_TABS} activeTab={statusFilter} onChange={setStatusFilter} />
      </div>

      {/* Desktop View (100% strictly preserved existing UI & behavior) */}
      <div className={styles.desktopView}>
        <Table<TicketPayment>
          columns={columns}
          data={ticketPayments}
          progressPending={isFetching}
          header={{
            search,
            setSearch,
            searchPlaceholder: "Search orders",
            formConfig,
            onFilterSubmit: (data) => {
              setGatheringFilter((data.eventId as string | undefined) ?? null);
              setPaymentTypeFilter((data.paymentType as string | undefined) ?? null);
              setPageSelected(1);
            },
            onExport: () => setIsExportModalOpen(true),
          }}
          paginationProps={{
            isPagination: true,
            pageSelected,
            totalCount: totalTicketPayments,
            rowsPerPageValue,
            setPageSelected,
          }}
        />
      </div>

      {/* Mobile View (Clean, responsive card-based UI <= 768px only) */}
      <div className={styles.mobileView}>
        {/* Mobile Toolbar: Search, Export & Filter Controls */}
        <div className={styles.mobileToolbar}>
          <div className={styles.mobileSearchRow}>
            <div className={styles.mobileSearchBox}>
              <SearchIcon className={styles.mobileSearchIcon} />
              <input
                type="text"
                placeholder="Search orders"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <CrossIcon
                  onClick={() => setSearch("")}
                  withBg={false}
                  height={12}
                  width={12}
                  strokeWidth={2}
                  className={styles.mobileCrossIcon}
                />
              )}
            </div>

            <Button
              outlined
              onClick={() => setIsExportModalOpen(true)}
              className={styles.mobileExportBtn}
            >
              <DownloadIcon />
              <span>EXPORT CSV</span>
            </Button>
          </div>

          <div className={styles.mobileFiltersRow}>
            <div className={styles.mobileFilterWrapper}>
              <select
                value={gatheringFilter ?? ""}
                onChange={(e) => {
                  setGatheringFilter(e.target.value || null);
                  setPageSelected(1);
                }}
                aria-label="Filter by gathering"
              >
                <option value="">Gathering: All</option>
                {events.map((event) => (
                  <option key={event.id} value={String(event.id)}>
                    {event.name}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className={styles.selectChevron} />
            </div>

            <div className={styles.mobileFilterWrapper}>
              <select
                value={paymentTypeFilter ?? ""}
                onChange={(e) => {
                  setPaymentTypeFilter(e.target.value || null);
                  setPageSelected(1);
                }}
                aria-label="Filter by payment method"
              >
                <option value="">Method: All</option>
                {PAYMENT_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <ChevronDownIcon className={styles.selectChevron} />
            </div>
          </div>
        </div>

        {/* Mobile Order Cards List */}
        <div className={styles.mobileCardList}>
          {isFetching ? (
            Array.from({ length: 4 }).map((_, idx) => (
              <div key={idx} className={styles.mobileSkeletonCard}>
                <div className={styles.skeletonHeader}>
                  <div className={styles.skeletonAvatar} />
                  <div className={styles.skeletonTextGroup}>
                    <div className={styles.skeletonLineShort} />
                    <div className={styles.skeletonLineLong} />
                  </div>
                </div>
                <div className={styles.skeletonBox} />
              </div>
            ))
          ) : ticketPayments.length === 0 ? (
            <div className={styles.mobileEmptyState}>
              <h4 className={styles.mobileEmptyTitle}>No orders found</h4>
              <p className={styles.mobileEmptyCaption}>
                {search || statusFilter || gatheringFilter || paymentTypeFilter
                  ? "No orders match your search or filter criteria."
                  : "No ticket orders available."}
              </p>
            </div>
          ) : (
            ticketPayments.map((order) => (
              <TicketCard
                key={order.id}
                order={order}
                actions={getRowActions(order)}
                statusTone={getOrderStatusTone(order.paymentStatus)}
                methodLabel={formatMethod(order)}
              />
            ))
          )}
        </div>

        {/* Mobile Pagination */}
        {totalTicketPayments > rowsPerPageValue && (
          <div className={styles.mobilePaginationContainer}>
            <Pagination
              pageSelected={pageSelected}
              totalCount={totalTicketPayments}
              rowsPerPageValue={rowsPerPageValue}
              setPageSelected={setPageSelected}
            />
          </div>
        )}
      </div>

      <ExportConfirmationModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        fallbackFileName="ticketing"
        fetchExportFile={() =>
          ticketPaymentService.exportTicketPayments({
            search: debouncedSearch || undefined,
            paymentStatus: statusFilter || undefined,
            eventId: gatheringFilter ? Number(gatheringFilter) : undefined,
            paymentType: paymentTypeFilter || undefined,
          })
        }
      />
    </>
  );
};

export default Ticketing;
